<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="admin-page">
    <!-- 頁首 -->
    <div class="admin-header">
      <div>
        <h1 class="admin-title">🪴 商品管理</h1>
        <p class="admin-sub">管理所有植栽商品</p>
      </div>
      <button class="btn-add" @click="openAdd">+ 新增商品</button>
    </div>

    <!-- 商品列表 -->
    <div class="admin-content">
      <div v-if="loading" class="loading">載入中...</div>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>圖片</th>
            <th>商品名稱</th>
            <th>拉丁名</th>
            <th>價格</th>
            <th>標籤</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in products" :key="p.id">
            <td class="td-img">
              <img
                v-if="p.imageUrl"
                :src="p.imageUrl"
                :alt="p.name"
                class="table-img"
              />
              <span v-else class="td-emoji">{{ p.emoji }}</span>
            </td>
            <td>
              <div class="td-name">{{ p.name }}</div>
              <div class="td-caption">{{ p.caption }}</div>
            </td>
            <td class="td-latin">{{ p.latinName }}</td>
            <td class="td-price">NT$ {{ p.price.toLocaleString() }}</td>
            <td>
              <span v-if="p.tag" class="tag">{{ p.tag }}</span>
            </td>
            <td>
              <span
                :class="[
                  'status',
                  p.isActive ? 'status-active' : 'status-inactive',
                ]"
              >
                {{ p.isActive ? "上架" : "下架" }}
              </span>
            </td>
            <td class="td-actions">
              <button class="btn-edit" @click="openEdit(p)">編輯</button>
              <button class="btn-delete" @click="deleteProduct(p)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 新增/編輯 Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ editMode ? "編輯商品" : "新增商品" }}</h2>
          <button class="modal-close" @click="closeModal">✕</button>
        </div>
        <div class="modal-body">
          <!-- 1. 名稱 + Emoji -->
          <div class="form-row">
            <div class="form-group">
              <label>商品名稱 *</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="例：編織發財樹"
              />
            </div>
            <div class="form-group">
              <label>Emoji（沒圖片時顯示）</label>
              <input v-model="form.emoji" type="text" placeholder="例：🌴" />
            </div>
          </div>

          <!-- 2. 拉丁名 -->
          <div class="form-group">
            <label>拉丁名</label>
            <input
              v-model="form.latinName"
              type="text"
              placeholder="例：Pachira macrocarpa"
            />
          </div>

          <!-- 3. 簡介標語 -->
          <div class="form-group">
            <label>簡介標語</label>
            <input
              v-model="form.caption"
              type="text"
              placeholder="例：招財旺運・制煞防護・低維護首選"
            />
          </div>

          <!-- 4. 價格 + 標籤 -->
          <div class="form-row">
            <div class="form-group">
              <label>價格 (NT$) *</label>
              <input
                v-model="form.price"
                type="number"
                placeholder="例：1280"
              />
            </div>
            <div class="form-group">
              <label>標籤</label>
              <input
                v-model="form.tag"
                type="text"
                placeholder="例：熱銷 No.1"
              />
            </div>
          </div>

          <!-- 5. 商品描述 -->
          <div class="form-group">
            <label>商品描述</label>
            <textarea
              v-model="form.description"
              placeholder="詳細描述..."
            ></textarea>
          </div>

          <!-- 6. 上架 -->
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.isActive" />
              上架販售
            </label>
          </div>

          <!-- 7. 圖片上傳 -->
          <div class="form-group">
            <label>商品圖片</label>
            <div class="image-upload-wrap">
              <div class="image-preview" v-if="form.imageUrl || previewUrl">
                <img :src="previewUrl || form.imageUrl" alt="商品圖片預覽" />
                <button class="image-remove" @click="removeImage">✕</button>
              </div>
              <div v-else class="image-placeholder" @click="triggerUpload">
                <span>📷</span>
                <p>點擊上傳圖片</p>
                <p class="image-hint">JPG、PNG、WebP，最大 5MB</p>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="handleImageUpload"
              />
              <button
                v-if="!form.imageUrl && !previewUrl"
                class="btn-upload"
                @click="triggerUpload"
              >
                {{ uploading ? "上傳中..." : "選擇圖片" }}
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">取消</button>
          <button
            class="btn-save"
            @click="saveProduct"
            :disabled="saving || uploading"
          >
            {{ saving ? "儲存中..." : "儲存" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import axios from "axios";

const products = ref([]);
const loading = ref(true);
const showModal = ref(false);
const editMode = ref(false);
const saving = ref(false);
const fileInput = ref(null);
const previewUrl = ref("");
const uploading = ref(false);

const form = reactive({
  id: null,
  name: "",
  latinName: "",
  caption: "",
  price: 0,
  emoji: "🌿",
  tag: "",
  description: "",
  isActive: true,
  imageUrl: "",
});

const resetForm = () => {
  form.id = null;
  form.name = "";
  form.latinName = "";
  form.caption = "";
  form.price = 0;
  form.emoji = "🌿";
  form.tag = "";
  form.description = "";
  form.isActive = true;
  form.imageUrl = "";
  previewUrl.value = "";
};

const loadProducts = async () => {
  try {
    const response = await axios.get("/products");
    products.value = response.data;
  } catch (error) {
    console.error("載入失敗:", error);
  } finally {
    loading.value = false;
  }
};

const openAdd = () => {
  resetForm();
  editMode.value = false;
  showModal.value = true;
};

const openEdit = (product) => {
  Object.assign(form, product);
  previewUrl.value = "";
  editMode.value = true;
  showModal.value = true;
};

const closeModal = () => {
  const hasUnsavedChanges = form.name || form.imageUrl || previewUrl.value;
  if (hasUnsavedChanges && !confirm("確定要放棄目前編輯的內容嗎？")) {
    return;
  }
  showModal.value = false;
  resetForm();
};

const triggerUpload = () => {
  fileInput.value.click();
};

const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  previewUrl.value = URL.createObjectURL(file);

  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post("/upload/image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    form.imageUrl = response.data.url;
  } catch (error) {
    console.error("上傳失敗:", error);
    alert("圖片上傳失敗，請稍後重試");
    previewUrl.value = "";
  } finally {
    uploading.value = false;
  }
};

const removeImage = () => {
  form.imageUrl = "";
  previewUrl.value = "";
  if (fileInput.value) fileInput.value.value = "";
};

const saveProduct = async () => {
  if (!form.name) {
    alert("請填寫商品名稱");
    return;
  }

  saving.value = true;
  try {
    if (editMode.value) {
      await axios.put(`/products/${form.id}`, form);
      alert("商品已更新！");
    } else {
      // 新增時不傳 id
      // eslint-disable-next-line no-unused-vars
      const { id, ...newProduct } = form;
      await axios.post("/products", newProduct);
      alert("商品已新增！");
    }
    closeModal();
    await loadProducts();
  } catch (error) {
    console.error("儲存失敗:", error);
    alert("儲存失敗，請稍後重試");
  } finally {
    saving.value = false;
  }
};

const deleteProduct = async (product) => {
  if (!confirm(`確定要刪除「${product.name}」嗎？`)) return;

  try {
    await axios.delete(`/products/${product.id}`);
    alert("商品已刪除！");
    await loadProducts();
  } catch (error) {
    console.error("刪除失敗:", error);
    alert("刪除失敗，請稍後重試");
  }
};

onMounted(() => {
  loadProducts();
});
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.admin-page {
  padding: 40px;
  font-family: "Noto Sans TC", "Microsoft JhengHei", sans-serif;
}
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}
.admin-title {
  font-size: 1.8em;
  color: #1c2b1a;
  margin-bottom: 4px;
}
.admin-sub {
  color: #6b7f68;
  font-size: 14px;
}
.btn-add {
  padding: 12px 24px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}
.btn-add:hover {
  background: #2d5016;
}
/* 原本: overflow: hidden; 改成 auto,允許表格必要時橫向捲動 */
.admin-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow-x: auto;
}
.admin-table {
  width: 100%;
  min-width: 820px;
  table-layout: fixed;
  border-collapse: collapse;
}
.admin-table col-img {
  width: 70px;
}
.admin-table th:nth-child(1),
.admin-table td:nth-child(1) {
  width: 70px;
}
.admin-table th:nth-child(2),
.admin-table td:nth-child(2) {
  width: 26%;
}
.admin-table th:nth-child(3),
.admin-table td:nth-child(3) {
  width: 16%;
}
.admin-table th:nth-child(4),
.admin-table td:nth-child(4) {
  width: 110px;
}
.admin-table th:nth-child(5),
.admin-table td:nth-child(5) {
  width: 110px;
}
.admin-table th:nth-child(6),
.admin-table td:nth-child(6) {
  width: 80px;
}
.admin-table th:nth-child(7),
.admin-table td:nth-child(7) {
  width: 130px;
}
.admin-table th {
  background: #f5f7fa;
  padding: 14px 16px;
  text-align: left;
  font-size: 13px;
  color: #6b7f68;
  font-weight: 600;
  border-bottom: 1px solid #eee;
}
.admin-table td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.admin-table tr:last-child td {
  border-bottom: none;
}
.admin-table tr:hover td {
  background: #f9fafb;
}
.td-img {
  width: 60px;
  text-align: center;
}
.table-img {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
}
.td-emoji {
  font-size: 28px;
}
.td-name {
  font-weight: 600;
  color: #1c2b1a;
  margin-bottom: 4px;
}
.td-caption {
  font-size: 12px;
  color: #9baa97;
}
.td-latin {
  font-style: italic;
  color: #9baa97;
  font-size: 13px;
}
.td-price {
  font-weight: 600;
  color: #2d5016;
}
.tag {
  background: #ff6b6b;
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  white-space: nowrap;
  display: inline-block;
}
.status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}
.status-active {
  background: #eaf2e0;
  color: #4e7b4f;
}
.status-inactive {
  background: #fee;
  color: #ff6b6b;
}
.td-actions {
  display: flex;
  gap: 8px;
  white-space: nowrap;
}
.btn-edit {
  padding: 6px 16px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-edit:hover {
  background: #5a6fd6;
}
.btn-delete {
  padding: 6px 16px;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.btn-delete:hover {
  background: #ff5252;
}
.loading {
  padding: 60px;
  text-align: center;
  color: #9baa97;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: white;
  border-radius: 16px;
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}
.modal-header h2 {
  font-size: 18px;
  color: #1c2b1a;
}
.modal-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #9baa97;
}
.modal-body {
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #3d4f3a;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px !important;
  font-weight: 500 !important;
}
.form-group input[type="text"],
.form-group input[type="number"],
.form-group textarea {
  padding: 10px 14px;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
}
.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4e7b4f;
}
.form-group textarea {
  min-height: 100px;
  resize: vertical;
}
.image-upload-wrap {
  border: 2px dashed #ddd;
  border-radius: 12px;
  overflow: hidden;
}
.image-preview {
  position: relative;
  width: 100%;
  max-height: 200px;
}
.image-preview img {
  width: 100%;
  height: 200px;
  object-fit: cover;
}
.image-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 14px;
}
.image-placeholder {
  padding: 30px 20px;
  text-align: center;
  cursor: pointer;
}
.image-placeholder:hover {
  background: #f9fafb;
}
.image-placeholder span {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}
.image-placeholder p {
  font-size: 14px;
  color: #6b7f68;
  margin-bottom: 4px;
}
.image-hint {
  font-size: 12px !important;
  color: #9baa97 !important;
}
.btn-upload {
  width: 100%;
  padding: 10px;
  background: #f5f7fa;
  color: #4e7b4f;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  font-weight: 600;
}
.btn-upload:hover {
  background: #eaf2e0;
}
.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  background: white;
}
.btn-cancel {
  padding: 10px 24px;
  background: #f5f7fa;
  color: #6b7f68;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.btn-save {
  padding: 10px 24px;
  background: #4e7b4f;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}
.btn-save:hover:not(:disabled) {
  background: #2d5016;
}
.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
